import { useState, useEffect, useCallback } from 'react';
import { thingsboardService } from '../services/thingsboard';
import { EditorZone } from '../components/FloorPlanEditor';
import { normalizeZonePoints, getBoundingBox } from '../utils/polygonUtils';

export function useEditorZones(apartmentAssetId: string | null = null) {
  const [zones, setZones] = useState<EditorZone[]>([]);
  const [primaryZoneId, setPrimaryZoneId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHierarchy = useCallback(async () => {
    if (!apartmentAssetId || apartmentAssetId === '00000000-0000-0000-0000-000000000000') {
      setZones([]);
      setPrimaryZoneId(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // 0 & 1. Fetch Apartment attributes and Zone relations in parallel
      const [aptAttrs, zoneRelations] = await Promise.all([
        thingsboardService.getAttributes('ASSET', apartmentAssetId, 'primaryZoneId', 'SERVER_SCOPE').catch(e => {
          console.warn('Could not fetch apartment attributes', e);
          return [];
        }),
        thingsboardService.getRelations('ASSET', apartmentAssetId)
      ]);

      const primaryZoneAttr = aptAttrs.find((a: any) => a.key === 'primaryZoneId');
      if (primaryZoneAttr && primaryZoneAttr.value) {
        setPrimaryZoneId(primaryZoneAttr.value);
      }

      const zoneAssets = zoneRelations.filter((r: any) => r.type === 'Contains' && r.to.entityType === 'ASSET');

      // 2. Fetch all Zones data in parallel
      const loadedZones: EditorZone[] = await Promise.all(zoneAssets.map(async (zRel: any) => {
        const zoneId = zRel.to.id;

        // Fetch Zone attributes and Device relations in parallel
        const [zoneAttrs, deviceRelations] = await Promise.all([
          thingsboardService.getAttributes('ASSET', zoneId, 'top,left,width,height,points,polygon,perimeter,primary_sensor', 'SERVER_SCOPE').catch(e => {
            console.warn(`Could not fetch attributes for zone ${zoneId}`, e);
            return [];
          }),
          thingsboardService.getRelations('ASSET', zoneId)
        ]);

        const getAttr = (key: string, def: any) => {
          const attr = zoneAttrs.find((a: any) => a.key === key);
          return attr ? attr.value : def;
        };

        const deviceEntities = deviceRelations.filter((r: any) => r.type === 'Contains' && r.to.entityType === 'DEVICE');

        // 3. Fetch all Devices data in parallel
        const sensors = await Promise.all(deviceEntities.map(async (dRel: any) => {
          const deviceId = dRel.to.id;

          // Fetch Device info and attributes in parallel
          const [deviceInfo, deviceAttrs] = await Promise.all([
            thingsboardService.getDevice(deviceId).catch(e => {
              console.warn(`Could not fetch device info for ${deviceId}`, e);
              return {};
            }),
            thingsboardService.getAttributes('DEVICE', deviceId, 'x,y,active,primary_appliance', 'SERVER_SCOPE').catch(e => {
              console.warn(`Could not fetch attributes for device ${deviceId}`, e);
              return [];
            })
          ]);

          const getDevAttr = (key: string, def: any) => {
            const attr = deviceAttrs.find((a: any) => a.key === key);
            return attr ? attr.value : def;
          };

          return {
            id: deviceId,
            type: deviceInfo.type || 'SEO',
            label: deviceInfo.name || dRel.toName || 'Unnamed Sensor',
            alias: deviceInfo.label,
            x: parseFloat(getDevAttr('x', '0')),
            y: parseFloat(getDevAttr('y', '0')),
            active: getDevAttr('active', false) === 'true' || getDevAttr('active', false) === true,
            primary_appliance: getDevAttr('primary_appliance', '')
          };
        }));

        const rawTop = parseFloat(getAttr('top', '0'));
        const rawLeft = parseFloat(getAttr('left', '0'));
        const rawWidth = parseFloat(getAttr('width', '20'));
        const rawHeight = parseFloat(getAttr('height', '20'));
        const rawPoints = getAttr('points', getAttr('polygon', null));
        const rawPerimeter = getAttr('perimeter', null);

        const points = normalizeZonePoints(
          rawPoints,
          {
            top: rawTop,
            left: rawLeft,
            width: rawWidth,
            height: rawHeight
          },
          rawPerimeter
        );
        const bbox = getBoundingBox(points);

        return {
          id: zoneId,
          name: zRel.toName || 'Unknown Zone',
          top: bbox.top,
          left: bbox.left,
          width: bbox.width,
          height: bbox.height,
          points: points,
          primary_sensor: getAttr('primary_sensor', null),
          sensors: sensors
        };
      }));

      setZones(loadedZones);
    } catch (err: any) {
      console.error('Failed to load Floor Plan Hierarchy', err);
      setError(err.message || 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [apartmentAssetId]);

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  return { zones, primaryZoneId, loading, error, setZones, setPrimaryZoneId, refetch: loadHierarchy };
}
